import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Trash2, Pencil, Search, Filter, X, Send, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDate } from "../lib/format";
import { PageIntro, PanelHead, Modal, Field, Badge } from "../components/UI";

const TABS = [
  { id: "outlets", label: "Outlets" },
  { id: "uoms", label: "Units of measure" },
  { id: "email-recipients", label: "Email recipients" },
  { id: "email-logs", label: "Email logs" },
  { id: "login-activity", label: "Login activity" },
];

export default function SettingsPage() {
  const [tab, setTab] = useState("outlets");
  return (
    <>
      <PageIntro
        eyebrow="Administration · system"
        title="System settings"
        subtitle="Manage master lists (outlets, UOMs), email notifications, and audit user access."
        testid="settings-title"
      />
      <section className="panel">
        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={t.id === tab}
              data-testid={`settings-tab-${t.id}`}
              className={`chip ${t.id === tab ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>
      {tab === "outlets" && <OutletsPanel />}
      {tab === "uoms" && <UomsPanel />}
      {tab === "email-recipients" && <EmailRecipientsPanel />}
      {tab === "email-logs" && <EmailLogsPanel />}
      {tab === "login-activity" && <LoginActivityPanel />}
    </>
  );
}

/* --------------------------------- Outlets -------------------------------- */
const OUTLET_TYPES = [
  "warehouse", "kitchen", "bar", "restaurant", "housekeeping", "office", "department", "outlet",
];

function OutletsPanel() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null); // { mode: "new"|"edit" }
  const [form, setForm] = useState({
    code: "", name: "", type: "outlet", active: true,
    approval_flow: "", manager_username: "",
  });
  const load = () => api.get("/outlets").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const openNew = () => {
    setForm({ code: "", name: "", type: "outlet", active: true, approval_flow: "", manager_username: "" });
    setModal({ mode: "new" });
  };
  const openEdit = (o) => {
    setForm({
      id: o.id, code: o.code, name: o.name, type: o.type || "outlet",
      active: o.active !== false,
      approval_flow: (o.approval_flow || []).join(", "),
      manager_username: o.manager_username || "",
    });
    setModal({ mode: "edit" });
  };
  const save = async (e) => {
    e.preventDefault();
    const flow = form.approval_flow.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    try {
      if (modal.mode === "new") {
        await api.post("/outlets", {
          code: form.code, name: form.name, type: form.type,
          active: form.active, approval_flow: flow,
          manager_username: form.manager_username,
        });
        toast.success(`Outlet ${form.code} added`);
      } else {
        await api.patch(`/outlets/${form.id}`, {
          name: form.name, type: form.type, active: form.active,
          approval_flow: flow, manager_username: form.manager_username,
        });
        toast.success(`Outlet ${form.code} updated`);
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const remove = async (o) => {
    if (!window.confirm(`Delete outlet ${o.code}?`)) return;
    try { await api.delete(`/outlets/${o.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  return (
    <section className="panel">
      <PanelHead title="Outlets" detail={`${rows.length} outlet(s) — used for PR, GRN, Store Request, Reports`} />
      <div className="toolbar">
        <button data-testid="outlet-add-button" className="primary-button" onClick={openNew}>
          <Plus size={17} /> Add outlet
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Code</th><th>Name</th><th>Type</th><th>Approval flow</th><th>Manager</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", padding: 40 }}>No outlets.</td></tr>}
            {rows.map((o) => (
              <tr key={o.id} data-testid={`outlet-row-${o.id}`}>
                <td><code>{o.code}</code></td>
                <td><strong>{o.name}</strong></td>
                <td><small>{o.type || "-"}</small></td>
                <td><small>Requester → {(o.approval_flow || []).map((r) => r.toUpperCase()).join(" → ") || "—"}</small></td>
                <td>{o.manager_username || <small>—</small>}</td>
                <td>{o.active !== false ? <Badge tone="green">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}</td>
                <td style={{ textAlign: "right" }}>
                  <button data-testid={`outlet-edit-${o.id}`} className="small-button" onClick={() => openEdit(o)}><Pencil size={12} /> Edit</button>
                  {o.code !== "main_wh" && (
                    <button data-testid={`outlet-delete-${o.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => remove(o)}><Trash2 size={12} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <Modal title={modal.mode === "new" ? "New outlet" : `Edit ${form.code}`} onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={save}>
            <Field label="Code" testid="outlet-code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} placeholder="e.g. kitchen" disabled={modal.mode === "edit"} />
            <Field label="Name" testid="outlet-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. Kitchen" />
            <label className="field">
              <span>Type</span>
              <select
                data-testid="outlet-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                {OUTLET_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <Field label="Manager username (optional)" testid="outlet-manager" value={form.manager_username} onChange={(v) => setForm({ ...form, manager_username: v })} placeholder="e.g. sari" />
            <Field label="Approval flow (roles, comma separated)" testid="outlet-flow" value={form.approval_flow} onChange={(v) => setForm({ ...form, approval_flow: v })} placeholder="finance" />
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Active</span>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" data-testid="outlet-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                <small>Only active outlets appear in PR / GRN / Store Request forms</small>
              </label>
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" data-testid="outlet-save" className="primary-button"><Check size={16} /> Save</button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

/* ---------------------------------- UOMs ---------------------------------- */
function UomsPanel() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", active: true, is_base: false });
  const load = () => api.get("/uoms").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm({ code: "", name: "", active: true, is_base: false }); setModal({ mode: "new" }); };
  const openEdit = (u) => { setForm({ id: u.id, code: u.code, name: u.name, active: !!u.active, is_base: !!u.is_base }); setModal({ mode: "edit" }); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal.mode === "new") {
        await api.post("/uoms", form); toast.success(`UOM ${form.code} added`);
      } else {
        await api.patch(`/uoms/${form.id}`, { name: form.name, active: form.active, is_base: form.is_base }); toast.success(`UOM ${form.code} updated`);
      }
      setModal(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete UOM ${u.code}?`)) return;
    try { await api.delete(`/uoms/${u.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  return (
    <section className="panel">
      <PanelHead title="Units of measure (UOM)" detail={`${rows.length} unit(s) — used across items, PR, PO, GRN, issues`} />
      <div className="toolbar">
        <button data-testid="uom-add-button" className="primary-button" onClick={openNew}><Plus size={17} /> Add UOM</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Code</th><th>Name</th><th>Base</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", padding: 40 }}>No UOMs.</td></tr>}
            {rows.map((u) => (
              <tr key={u.id} data-testid={`uom-row-${u.id}`}>
                <td><code>{u.code}</code></td>
                <td><strong>{u.name}</strong></td>
                <td>{u.is_base ? <Badge tone="blue">Base</Badge> : <small>—</small>}</td>
                <td>{u.active ? <Badge tone="green">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}</td>
                <td style={{ textAlign: "right" }}>
                  <button data-testid={`uom-edit-${u.id}`} className="small-button" onClick={() => openEdit(u)}><Pencil size={12} /> Edit</button>
                  <button data-testid={`uom-delete-${u.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => remove(u)}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <Modal title={modal.mode === "new" ? "New UOM" : `Edit ${form.code}`} onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={save}>
            <Field label="Code" testid="uom-code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} placeholder="e.g. KG" disabled={modal.mode === "edit"} />
            <Field label="Name" testid="uom-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. Kilogram" />
            <label className="field">
              <span>Active</span>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" data-testid="uom-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                <small>Only active UOMs appear in dropdowns</small>
              </label>
            </label>
            <label className="field">
              <span>Base unit</span>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" data-testid="uom-base" checked={form.is_base} onChange={(e) => setForm({ ...form, is_base: e.target.checked })} />
                <small>Mark as your base unit (e.g. PCS)</small>
              </label>
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" data-testid="uom-save" className="primary-button"><Check size={16} /> Save</button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

/* -------------------------------- Login log ------------------------------- */
function LoginActivityPanel() {
  const [rows, setRows] = useState([]);
  const [username, setUsername] = useState("");
  const [onlyFailed, setOnlyFailed] = useState(false);
  const load = () => {
    const p = new URLSearchParams();
    p.set("limit", "200");
    if (username.trim()) p.set("username", username.trim());
    if (onlyFailed) p.set("only_failed", "true");
    return api.get(`/login-activity?${p.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [onlyFailed]); // eslint-disable-line
  const succeeded = useMemo(() => rows.filter((r) => r.success).length, [rows]);
  const failed = rows.length - succeeded;
  return (
    <section className="panel">
      <PanelHead
        title="Login activity"
        detail={`${rows.length} recent event(s) · ${succeeded} success · ${failed} failed`}
      />
      <div className="toolbar" style={{ gap: 8, alignItems: "center" }}>
        <div className="search-box">
          <Search size={17} />
          <input
            data-testid="login-activity-search"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Filter by username..."
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
        </div>
        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="checkbox"
            data-testid="login-activity-failed-only"
            checked={onlyFailed}
            onChange={(e) => setOnlyFailed(e.target.checked)}
          />
          <small>Failed attempts only</small>
        </label>
        <button className="secondary-button" onClick={load} data-testid="login-activity-refresh">
          <Filter size={14} /> Apply
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>When</th><th>Username</th><th>Result</th><th>Reason</th><th>IP</th><th>User agent</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 40 }}>No login events recorded yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`login-event-${r.id}`}>
                <td><small>{formatDate(r.at)}</small></td>
                <td><code>{r.username}</code></td>
                <td>{r.success ? <Badge tone="green">Success</Badge> : <Badge tone="amber">Failed</Badge>}</td>
                <td><small>{r.reason}</small></td>
                <td><code>{r.ip || "-"}</code></td>
                <td><small style={{ maxWidth: 320, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis" }}>{r.user_agent || "-"}</small></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* --------------------------- Email recipients ---------------------------- */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function EmailRecipientsPanel() {
  const [rows, setRows] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [modal, setModal] = useState(null); // { mode, id }
  const [form, setForm] = useState({ outlet_code: "", emails: [], active: true });
  const [draft, setDraft] = useState("");

  const load = () => api.get("/email-recipients").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/outlets?active=true").then((r) => setOutlets(r.data));
  }, []);

  const usedOutlets = useMemo(() => new Set(rows.map((r) => r.outlet_code)), [rows]);

  const openNew = () => {
    setForm({ outlet_code: "", emails: [], active: true });
    setDraft("");
    setModal({ mode: "new" });
  };
  const openEdit = (r) => {
    setForm({ outlet_code: r.outlet_code, emails: [...(r.emails || [])], active: r.active !== false });
    setDraft("");
    setModal({ mode: "edit", id: r.id });
  };

  const addEmail = (val) => {
    const e = (val || "").trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RE.test(e)) return toast.error(`Format email tidak valid: ${e}`);
    if (form.emails.includes(e)) { setDraft(""); return; }
    setForm((f) => ({ ...f, emails: [...f.emails, e] }));
    setDraft("");
  };
  const removeEmail = (e) => setForm((f) => ({ ...f, emails: f.emails.filter((x) => x !== e) }));

  const save = async (ev) => {
    ev.preventDefault();
    if (modal.mode === "new" && !form.outlet_code) return toast.error("Pilih outlet");
    if (form.emails.length === 0) return toast.error("Minimal 1 email");
    try {
      if (modal.mode === "new") {
        await api.post("/email-recipients", form);
        toast.success("Setting penerima ditambahkan");
      } else {
        await api.patch(`/email-recipients/${modal.id}`, { emails: form.emails, active: form.active });
        toast.success("Setting diperbarui");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/email-recipients/${r.id}`, { active: !(r.active !== false) });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Hapus setting untuk ${r.outlet_name}?`)) return;
    try { await api.delete(`/email-recipients/${r.id}`); toast.success("Dihapus"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const sendTest = async (r) => {
    try {
      await api.post(`/email-recipients/${r.id}/test`);
      toast.success(`Test email dikirim ke ${(r.emails || []).length} alamat (cek Email logs)`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const availableOutlets = outlets.filter((o) => !usedOutlets.has(o.code));

  return (
    <section className="panel">
      <PanelHead title="Email notification recipients" detail={`${rows.length} outlet(s) configured — goods received & PR approval emails`} />
      <div className="toolbar">
        <button data-testid="email-recipient-add-button" className="primary-button" onClick={openNew}>
          <Plus size={17} /> Add recipient setting
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Outlet</th><th>Emails</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", padding: 40 }}>Belum ada setting penerima email.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`email-recipient-row-${r.id}`}>
                <td><strong>{r.outlet_name}</strong><br /><code>{r.outlet_code}</code></td>
                <td>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(r.emails || []).map((e) => <span key={e} className="chip small"><Mail size={11} /> {e}</span>)}
                  </div>
                </td>
                <td>
                  <button className="small-button" data-testid={`email-recipient-toggle-${r.id}`} onClick={() => toggleActive(r)}>
                    {r.active !== false ? <Badge tone="green">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}
                  </button>
                </td>
                <td style={{ textAlign: "right" }}>
                  <button data-testid={`email-recipient-test-${r.id}`} className="small-button" onClick={() => sendTest(r)}><Send size={12} /> Test</button>
                  <button data-testid={`email-recipient-edit-${r.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => openEdit(r)}><Pencil size={12} /> Edit</button>
                  <button data-testid={`email-recipient-delete-${r.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => remove(r)}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <Modal title={modal.mode === "new" ? "New recipient setting" : `Edit ${form.outlet_code}`} onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={save}>
            {modal.mode === "new" ? (
              <label className="field">
                <span>Outlet</span>
                <select data-testid="email-recipient-outlet" value={form.outlet_code} onChange={(e) => setForm({ ...form, outlet_code: e.target.value })}>
                  <option value="">— pilih outlet —</option>
                  {availableOutlets.map((o) => <option key={o.code} value={o.code}>{o.name} ({o.code})</option>)}
                </select>
              </label>
            ) : (
              <Field label="Outlet" value={form.outlet_code} onChange={() => {}} disabled />
            )}
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Emails (tekan Enter atau koma untuk menambah)</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: 8, border: "1px solid var(--line)", borderRadius: 8, minHeight: 44 }}>
                {form.emails.map((e) => (
                  <span key={e} className="chip small" style={{ gap: 4 }}>
                    {e}
                    <button type="button" onClick={() => removeEmail(e)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--muted)" }}><X size={12} /></button>
                  </span>
                ))}
                <input
                  data-testid="email-recipient-email-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmail(draft); }
                    else if (e.key === "Backspace" && !draft && form.emails.length) removeEmail(form.emails[form.emails.length - 1]);
                  }}
                  onBlur={() => draft && addEmail(draft)}
                  placeholder={form.emails.length ? "" : "nama@domain.com"}
                  style={{ border: "none", outline: "none", flex: 1, minWidth: 140, background: "transparent" }}
                />
              </div>
            </label>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Active</span>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" data-testid="email-recipient-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                <small>Email hanya dikirim bila setting aktif</small>
              </label>
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" data-testid="email-recipient-save" className="primary-button"><Check size={16} /> Save</button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

/* ------------------------------- Email logs ------------------------------- */
const LOG_TONE = { sent: "green", failed: "amber", skipped: "neutral" };
const TYPE_LABEL = {
  goods_received: "Goods received", pr_pending: "PR approval", pr_reminder: "PR reminder",
  test: "Test", pr_submitted: "PR submitted", po_created: "PO created", sr_created: "SR created",
};

function EmailLogsPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/email-logs?limit=200").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <section className="panel">
      <PanelHead title="Email logs" detail={`${rows.length} recent email(s)`} />
      <div className="toolbar">
        <button className="secondary-button" onClick={load} data-testid="email-logs-refresh"><Filter size={14} /> Refresh</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>When</th><th>Type</th><th>To</th><th>Subject</th><th>Status</th><th>Error</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 40 }}>Belum ada email terkirim.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`email-log-${r.id}`}>
                <td><small>{formatDate(r.created_at)}</small></td>
                <td><small>{TYPE_LABEL[r.type] || r.type || "-"}</small></td>
                <td><small>{(r.to || []).join(", ") || "—"}</small></td>
                <td><small>{r.subject}</small></td>
                <td><Badge tone={LOG_TONE[r.status] || "neutral"}>{r.status}</Badge></td>
                <td><small style={{ color: "var(--amber)", maxWidth: 260, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis" }}>{r.error || ""}</small></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

